import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Newspaper, MessageSquare, Briefcase, TrendingUp, Plus, Edit2, Trash2, Eye, EyeOff, MessagesSquare, ChevronRight, User, X, AlertTriangle, Mail, Star, Users, UserCheck, Clock, Building2, DollarSign, FileText, Download, Upload } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Toast from '../components/admin/Toast';
import ConfirmDialog from '../components/admin/ConfirmDialog';
import LoginForm from '../components/admin/LoginForm';
import RetentionReferences from '../components/admin/RetentionReferences';
import { absoluteMediaUrl, apiFetch } from '../lib/apiClient';
import { dataBackend, supabase } from '../lib/supabaseClient';
import { localDateTimeValue, withArticleImage } from '../lib/articleFields';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Dashboard Component
const AdminDashboard = ({ token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const activeContentTab = useRef(activeTab);
  const contentRequestVersion = useRef(0);
  useEffect(() => {
    activeContentTab.current = activeTab;
    contentRequestVersion.current += 1;
    setItems([]);
    setShowForm(false);
    setEditItem(null);
  }, [activeTab]);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  
  // Toast and Dialog states
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'news', label: 'News', icon: Newspaper },
    { id: 'services', label: 'Servizi', icon: Briefcase },
    { id: 'results', label: 'Risultati', icon: TrendingUp },
    { id: 'testimonials', label: 'Testimonianze', icon: Star },
    { id: 'requests', label: 'Richieste', icon: UserCheck },
    { id: 'chatbot', label: 'Chatbot', icon: MessagesSquare },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    ...(dataBackend === 'supabase' ? [{ id: 'retention', label: 'Conservazione', icon: Clock }] : []),
  ];

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [newsletterStats, setNewsletterStats] = useState({ total_subscribers: 0, unsubscribed: 0 });
  const [showNewsletterForm, setShowNewsletterForm] = useState(false);
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterContent, setNewsletterContent] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterPreviewAccepted, setNewsletterPreviewAccepted] = useState(false);
  const [newsletterPreview, setNewsletterPreview] = useState(null);
  const newsletterKey = useRef(crypto.randomUUID());
  
  // Contact Requests State
  const [contactRequests, setContactRequests] = useState([]);
  const [contactStats, setContactStats] = useState({ total: 0, new: 0, read: 0, contacted: 0, archived: 0 });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestFilter, setRequestFilter] = useState('all');

  const fetchConversations = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setConversations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore caricamento conversazioni:', err);
      setConversations([]);
    }
  };

  const fetchConversationDetail = async (sessionId) => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/conversations/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setSelectedConversation(data);
    } catch (err) {
      console.error('Errore caricamento dettaglio:', err);
    }
  };

  const deleteConversation = async (sessionId) => {
    showConfirm(
      'Elimina Conversazione',
      'Sei sicuro di voler eliminare questa conversazione? Questa azione non può essere annullata.',
      async () => {
        closeConfirm();
        try {
          await apiFetch(`${API_URL}/api/admin/conversations/${sessionId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          fetchConversations();
          setSelectedConversation(null);
          showToast('Conversazione eliminata', 'success');
        } catch (err) {
          console.error('Errore eliminazione:', err);
          showToast('Errore durante l\'eliminazione', 'error');
        }
      }
    );
  };

  const fetchSubscribers = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/newsletter/subscribers`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setSubscribers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore caricamento iscritti:', err);
    }
  };

  const fetchNewsletterStats = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/newsletter/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setNewsletterStats(data);
    } catch (err) {
      console.error('Errore caricamento stats:', err);
    }
  };

  const deleteSubscriber = async (subscriberId) => {
    showConfirm(
      'Elimina Iscritto',
      'Sei sicuro di voler eliminare questo iscritto dalla newsletter?',
      async () => {
        closeConfirm();
        try {
          await apiFetch(`${API_URL}/api/admin/newsletter/subscriber/${subscriberId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          fetchSubscribers();
          fetchNewsletterStats();
          showToast('Iscritto eliminato', 'success');
        } catch (err) {
          showToast('Errore durante l\'eliminazione', 'error');
        }
      }
    );
  };

  const approveNewsletterPreview = async (accepted) => {
    setNewsletterPreviewAccepted(false);
    if (!accepted) return;
    if (dataBackend !== 'supabase') { setNewsletterPreviewAccepted(true); return; }
    try {
      const result = await apiFetch(`${API_URL}/api/admin/newsletter/preview`, {
        method: 'POST', body: JSON.stringify({ subject: newsletterSubject, content: newsletterContent }),
      });
      const preview = await result.json();
      if (!result.ok) throw new Error(preview.detail || 'Anteprima non disponibile');
      setNewsletterPreview(preview);
      setNewsletterPreviewAccepted(true);
    } catch (error) { showToast(error.message, 'error'); }
  };

  const sendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterContent.trim()) {
      showToast('Compila tutti i campi', 'error');
      return;
    }
    if (!newsletterPreviewAccepted) {
      showToast('Controlla e approva l’anteprima prima di accodare la newsletter', 'error');
      return;
    }
    
    setSendingNewsletter(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/newsletter/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Idempotency-Key': newsletterKey.current,
        },
        body: JSON.stringify({
          subject: newsletterSubject,
          content: newsletterContent,
          recipient_ids: newsletterPreview?.recipient_ids,
          preview_token: newsletterPreview?.preview_token,
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        showToast(data.message, 'success');
        setShowNewsletterForm(false);
        setNewsletterSubject('');
        setNewsletterContent('');
        setNewsletterPreviewAccepted(false);
        setNewsletterPreview(null);
        newsletterKey.current = crypto.randomUUID();
      } else {
        showToast(data.detail || 'Errore durante l\'invio', 'error');
      }
    } catch (err) {
      showToast('Errore di connessione', 'error');
    } finally {
      setSendingNewsletter(false);
    }
  };

  // Contact Requests Functions
  const fetchContactRequests = async () => {
    try {
      const url = requestFilter === 'all' 
        ? `${API_URL}/api/admin/contact-requests`
        : `${API_URL}/api/admin/contact-requests?status=${requestFilter}`;
      const response = await apiFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setContactRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore caricamento richieste:', err);
      setContactRequests([]);
    }
  };

  const fetchContactStats = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/contact-requests/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setContactStats(data);
    } catch (err) {
      console.error('Errore caricamento stats richieste:', err);
    }
  };

  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await apiFetch(`${API_URL}/api/admin/contact-requests/${requestId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchContactRequests();
      fetchContactStats();
      showToast('Stato aggiornato', 'success');
    } catch (err) {
      showToast('Errore durante l\'aggiornamento', 'error');
    }
  };

  const deleteContactRequest = async (requestId) => {
    showConfirm(
      'Elimina Richiesta',
      'Sei sicuro di voler eliminare questa richiesta?',
      async () => {
        closeConfirm();
        try {
          await apiFetch(`${API_URL}/api/admin/contact-requests/${requestId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          fetchContactRequests();
          fetchContactStats();
          setSelectedRequest(null);
          showToast('Richiesta eliminata', 'success');
        } catch (err) {
          showToast('Errore durante l\'eliminazione', 'error');
        }
      }
    );
  };

  const toggleFeatured = async (id) => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/news/${id}/featured`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      showToast(data.featured ? 'Articolo aggiunto alle letture consigliate' : 'Articolo rimosso dalle letture consigliate', 'success');
      fetchItems();
    } catch (err) {
      showToast('Errore durante l\'aggiornamento', 'error');
    }
  };

  const fetchItems = async () => {
    if (activeContentTab.current !== activeTab) return;
    const version = ++contentRequestVersion.current;
    const isCurrent = () => version === contentRequestVersion.current && activeContentTab.current === activeTab;
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/${activeTab}?published_only=false`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (isCurrent()) setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore caricamento:', err);
      if (isCurrent()) setItems([]);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const checkCalendarStatus = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/calendar/status`);
      const data = await response.json();
      setCalendarStatus(data.connected);
    } catch (err) {
      setCalendarStatus(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'chatbot') {
      fetchConversations();
    } else if (activeTab === 'newsletter') {
      fetchSubscribers();
      fetchNewsletterStats();
    } else if (activeTab === 'requests') {
      fetchContactRequests();
      fetchContactStats();
    } else if (['news', 'services', 'results', 'testimonials'].includes(activeTab)) {
      fetchItems();
    }
    checkCalendarStatus();
    setShowForm(false);
    setEditItem(null);
    setSelectedConversation(null);
    setSelectedRequest(null);
  }, [activeTab]);

  // Refetch contact requests when filter changes
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchContactRequests();
    }
  }, [requestFilter]);

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/dashboard/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardStats(data);
      }
    } catch (err) {
      console.error('Errore caricamento statistiche:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch chart data
  const fetchChartData = async () => {
    setChartLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/dashboard/chart-data?days=14`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setChartData(data.data || []);
      }
    } catch (err) {
      console.error('Errore caricamento dati grafico:', err);
    } finally {
      setChartLoading(false);
    }
  };

  // Fetch stats on dashboard tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardStats();
      fetchChartData();
    }
  }, [activeTab]);

  const handleImportData = async () => {
    if (!window.confirm('Vuoi importare i dati predefiniti? I dati esistenti non verranno sovrascritti.')) return;
    setImporting(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/import-data`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (!response.ok) {
        alert(`Errore: ${data.detail || 'Errore sconosciuto'}`);
        return;
      }
      
      showToast(`Importazione completata! News: ${data.imported.news}, Testimonianze: ${data.imported.testimonials}, Servizi: ${data.imported.services}, Risultati: ${data.imported.results}`, 'success');
      fetchItems();
    } catch (err) {
      console.error('Errore importazione:', err);
      showToast(`Errore durante l'importazione: ${err.message}`, 'error');
    } finally {
      setImporting(false);
    }
  };

  // Export articles to JSON file
  const handleExportArticles = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/export-articles`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (!response.ok) {
        showToast(`Errore esportazione: ${data.detail || 'Errore'}`, 'error');
        return;
      }
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `articoli_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast(`Esportati ${data.count} articoli`, 'success');
    } catch (err) {
      console.error('Errore export:', err);
      showToast(`Errore durante l'esportazione: ${err.message}`, 'error');
    }
  };

  // Import articles from JSON file
  const handleImportArticles = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.articles || !Array.isArray(data.articles)) {
        showToast('File JSON non valido: manca il campo "articles"', 'error');
        return;
      }
      
      const overwrite = window.confirm(
        `Trovati ${data.articles.length} articoli.\n\nVuoi sovrascrivere gli articoli esistenti con lo stesso titolo?\n\n- OK = Sovrascrivi\n- Annulla = Salta esistenti`
      );
      
      const response = await apiFetch(`${API_URL}/api/admin/import-articles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          articles: data.articles,
          overwrite: overwrite,
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        showToast(`Errore importazione: ${result.detail || 'Errore'}`, 'error');
        return;
      }
      
      showToast(
        `Importazione completata!\nNuovi: ${result.imported}, Aggiornati: ${result.updated}, Saltati: ${result.skipped}`,
        'success'
      );
      
      fetchItems();
    } catch (err) {
      console.error('Errore import articoli:', err);
      showToast(`Errore durante l'importazione: ${err.message}`, 'error');
    }
    
    // Reset input
    event.target.value = '';
  };

  // Cleanup demo articles
  const handleCleanupDemo = async () => {
    if (!window.confirm('Vuoi eliminare tutti gli articoli Demo e Test dal database?\n\nQuesta azione non può essere annullata.')) {
      return;
    }
    
    try {
      const response = await apiFetch(`${API_URL}/api/admin/news/cleanup-demo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        showToast(`Errore: ${result.detail || 'Errore'}`, 'error');
        return;
      }
      
      showToast(result.message, 'success');
      fetchItems();
    } catch (err) {
      console.error('Errore pulizia demo:', err);
      showToast(`Errore: ${err.message}`, 'error');
    }
  };

  const handleConnectCalendar = async () => {
    try {
      const response = await apiFetch(`${API_URL}/api/oauth/calendar/login`);
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (err) {
      console.error('Errore connessione calendario:', err);
      showToast('Errore nella connessione a Google Calendar', 'error');
    }
  };

  const handleDelete = async (id) => {
    showConfirm(
      'Elimina Elemento',
      'Sei sicuro di voler eliminare questo elemento? Questa azione non può essere annullata.',
      async () => {
        closeConfirm();
        try {
          const response = await apiFetch(`${API_URL}/api/admin/${activeTab}/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (!response.ok) {
            const error = await response.json().catch(() => null);
            showToast(error?.detail || 'Eliminazione non riuscita', 'error');
            return;
          }
          fetchItems();
          showToast('Elemento eliminato con successo', 'success');
        } catch (err) {
          console.error('Errore eliminazione:', err);
          showToast('Errore durante l\'eliminazione', 'error');
        }
      }
    );
  };

  const handleTogglePublish = async (id) => {
    try {
      const response = await apiFetch(`${API_URL}/api/admin/${activeTab}/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        showToast(error?.detail || 'Pubblicazione non riuscita', 'error');
        return;
      }
      fetchItems();
    } catch (err) {
      console.error('Errore toggle:', err);
    }
  };

  const handleSave = async (formData) => {
    try {
      const url = editItem 
        ? `${API_URL}/api/admin/${activeTab}/${editItem.id}`
        : `${API_URL}/api/admin/${activeTab}`;
      
      const response = await apiFetch(url, {
        method: editItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowForm(false);
        setEditItem(null);
        fetchItems();
        showToast('Contenuto salvato');
      } else {
        const error = await response.json().catch(() => null);
        showToast(error?.detail || 'Salvataggio non riuscito. Il contenuto non è stato salvato.', 'error');
      }
    } catch (err) {
      console.error('Errore salvataggio:', err);
      showToast('Connessione non riuscita. Il contenuto non è stato salvato.', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Toast */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
        confirmText="Elimina"
        cancelText="Annulla"
        type="danger"
      />

      {/* Header */}
      <header className="bg-gradient-to-r from-[#0b114e] to-[#2d4a6f] text-white px-6 py-5 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Pannello di Controllo</h1>
            <p className="text-white/60 text-sm mt-0.5">Gestione contenuti - Nicola Scerni</p>
          </div>
          <div className="flex items-center gap-6">
            {/* Calendar Status */}
            <div className="flex items-center gap-2 text-sm bg-white/10 px-4 py-2 rounded-full">
              <span className={`w-2 h-2 rounded-full ${calendarStatus ? 'bg-green-400 animate-pulse' : 'bg-yellow-400'}`} />
              <span className="text-white/80">
                {calendarStatus ? 'Calendar connesso' : 'Calendar non connesso'}
              </span>
              <button
                onClick={handleConnectCalendar}
                className="text-[#d4883a] hover:text-[#d4896a] font-medium ml-1"
              >
                {calendarStatus ? 'Ricollega' : 'Connetti'}
              </button>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2.5 px-5 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#d4883a] text-white shadow-lg shadow-[#d4883a]/30 scale-105'
                    : 'bg-white text-gray-600 hover:bg-gray-50 shadow-sm hover:shadow-md'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Actions - Hide for chatbot, newsletter, requests and dashboard tabs */}
        {!['chatbot', 'newsletter', 'requests', 'dashboard', 'retention'].includes(activeTab) && (
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[#0b114e] capitalize">{activeTab}</h2>
            <div className="flex items-center gap-3">
              {/* Export/Import Articles - Show only for news tab */}
              {activeTab === 'news' && (
                <>
                  <button
                    onClick={handleExportArticles}
                    className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl font-medium transition-all shadow-sm hover:shadow-md text-sm"
                    title="Esporta tutti gli articoli in JSON"
                  >
                    <Download className="w-4 h-4" />
                    Esporta
                  </button>
                  <label className="flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-xl font-medium transition-all shadow-sm hover:shadow-md cursor-pointer text-sm">
                    <Upload className="w-4 h-4" />
                    Importa
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleImportArticles}
                      className="hidden"
                    />
                  </label>
                  <button
                    onClick={handleCleanupDemo}
                    className="flex items-center gap-2 bg-red-50 border border-red-200 hover:bg-red-100 text-red-600 px-4 py-2.5 rounded-xl font-medium transition-all shadow-sm hover:shadow-md text-sm"
                    title="Elimina articoli demo/test"
                  >
                    <Trash2 className="w-4 h-4" />
                    Pulisci Demo
                  </button>
                </>
              )}
              <button
                onClick={() => { setShowForm(true); setEditItem(null); }}
                className="flex items-center gap-2 bg-gradient-to-r from-[#d4883a] to-[#d4896a] hover:from-[#d4896a] hover:to-[#d4883a] text-white px-5 py-2.5 rounded-xl font-medium transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <Plus className="w-5 h-5" />
                Aggiungi
              </button>
            </div>
          </div>
        )}

        {/* Dashboard View */}
        {activeTab === 'dashboard' ? (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0b114e]">Dashboard</h2>
              <button
                onClick={fetchDashboardStats}
                className="text-[#d4883a] hover:text-[#d4896a] font-medium text-sm flex items-center gap-1"
              >
                Aggiorna
              </button>
            </div>
            
            {statsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d4883a]"></div>
              </div>
            ) : dashboardStats ? (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Articoli */}
                  <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-[#d4883a]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Articoli Pubblicati</p>
                        <p className="text-3xl font-bold text-[#0b114e] mt-1">{dashboardStats.articles?.published || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">{dashboardStats.articles?.featured || 0} in evidenza</p>
                      </div>
                      <div className="w-12 h-12 bg-[#d4883a]/10 rounded-full flex items-center justify-center">
                        <Newspaper className="w-6 h-6 text-[#d4883a]" />
                      </div>
                    </div>
                  </div>

                  {/* Iscritti Newsletter */}
                  <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Iscritti Newsletter</p>
                        <p className="text-3xl font-bold text-[#0b114e] mt-1">{dashboardStats.newsletter?.active || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">{dashboardStats.newsletter?.unsubscribed || 0} disiscritti</p>
                      </div>
                      <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                        <Mail className="w-6 h-6 text-green-500" />
                      </div>
                    </div>
                  </div>

                  {/* Richieste Contatto */}
                  <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Richieste Contatto</p>
                        <p className="text-3xl font-bold text-[#0b114e] mt-1">{dashboardStats.contacts?.total || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">{dashboardStats.contacts?.new || 0} nuove da gestire</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                        <UserCheck className="w-6 h-6 text-blue-500" />
                      </div>
                    </div>
                  </div>

                  {/* Conversazioni Chatbot */}
                  <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-purple-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Chat Avviate</p>
                        <p className="text-3xl font-bold text-[#0b114e] mt-1">{dashboardStats.chatbot?.total_conversations || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">{dashboardStats.chatbot?.recent_7_days || 0} ultimi 7 giorni</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center">
                        <MessagesSquare className="w-6 h-6 text-purple-500" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-bold text-[#0b114e] mb-4">Azioni Rapide</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <button
                      onClick={() => setActiveTab('news')}
                      className="p-4 bg-gray-50 hover:bg-[#d4883a]/10 rounded-xl text-center transition-colors group"
                    >
                      <Plus className="w-6 h-6 mx-auto text-gray-400 group-hover:text-[#d4883a] transition-colors" />
                      <span className="text-sm font-medium text-gray-600 mt-2 block">Nuovo Articolo</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('requests')}
                      className="p-4 bg-gray-50 hover:bg-blue-50 rounded-xl text-center transition-colors group"
                    >
                      <UserCheck className="w-6 h-6 mx-auto text-gray-400 group-hover:text-blue-500 transition-colors" />
                      <span className="text-sm font-medium text-gray-600 mt-2 block">Vedi Richieste</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('chatbot')}
                      className="p-4 bg-gray-50 hover:bg-purple-50 rounded-xl text-center transition-colors group"
                    >
                      <MessagesSquare className="w-6 h-6 mx-auto text-gray-400 group-hover:text-purple-500 transition-colors" />
                      <span className="text-sm font-medium text-gray-600 mt-2 block">Vedi Chat</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('newsletter')}
                      className="p-4 bg-gray-50 hover:bg-green-50 rounded-xl text-center transition-colors group"
                    >
                      <Mail className="w-6 h-6 mx-auto text-gray-400 group-hover:text-green-500 transition-colors" />
                      <span className="text-sm font-medium text-gray-600 mt-2 block">Invia Newsletter</span>
                    </button>
                  </div>
                </div>

                {/* Recent Activity Summary */}
                <div className="bg-gradient-to-r from-[#0b114e] to-[#1a2a5e] rounded-xl p-6 text-white">
                  <h3 className="font-bold mb-4">Attività Ultimi 7 Giorni</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-white/60 text-sm">Nuove Richieste</p>
                      <p className="text-2xl font-bold">{dashboardStats.contacts?.recent_7_days || 0}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Conversazioni Chat</p>
                      <p className="text-2xl font-bold">{dashboardStats.chatbot?.recent_7_days || 0}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Appuntamenti</p>
                      <p className="text-2xl font-bold">{dashboardStats.appointments?.total || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Activity Chart */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="font-bold text-[#0b114e] mb-4">Andamento Ultime 2 Settimane</h3>
                  {chartLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d4883a]"></div>
                    </div>
                  ) : chartData.length > 0 ? (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="label" 
                            tick={{ fontSize: 12, fill: '#666' }}
                            tickLine={false}
                          />
                          <YAxis 
                            tick={{ fontSize: 12, fill: '#666' }}
                            tickLine={false}
                            axisLine={false}
                            allowDecimals={false}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}
                            labelStyle={{ fontWeight: 'bold', color: '#0b114e' }}
                          />
                          <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                            iconType="circle"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="contacts" 
                            name="Richieste Contatto"
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, fill: '#3b82f6' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="chats" 
                            name="Chat Avviate"
                            stroke="#8b5cf6" 
                            strokeWidth={2}
                            dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, fill: '#8b5cf6' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="subscribers" 
                            name="Nuovi Iscritti"
                            stroke="#22c55e" 
                            strokeWidth={2}
                            dot={{ fill: '#22c55e', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, fill: '#22c55e' }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      Nessun dato disponibile per il grafico
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Nessun dato disponibile
              </div>
            )}
          </div>
        ) : activeTab === 'chatbot' ? (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0b114e]">Conversazioni Chatbot</h2>
              <span className="text-gray-500 text-sm">{conversations.length} conversazioni</span>
            </div>
            
            {selectedConversation ? (
              // Conversation Detail View
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-b">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="text-[#d4883a] hover:text-[#d4896a] font-medium flex items-center gap-1"
                  >
                    ← Torna alla lista
                  </button>
                  <button
                    onClick={() => deleteConversation(selectedConversation.session_id)}
                    className="text-red-500 hover:text-red-600 flex items-center gap-1 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Elimina
                  </button>
                </div>
                <div className="p-6">
                  <div className="mb-4 text-sm text-gray-500">
                    Sessione: {selectedConversation.session_id} • 
                    Creata: {new Date(selectedConversation.created_at).toLocaleString('it-IT')}
                  </div>
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {(selectedConversation.messages || []).map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] px-4 py-3 rounded-2xl ${
                            msg.role === 'user'
                              ? 'bg-[#0b114e] text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          {msg.timestamp && (
                            <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-white/60' : 'text-gray-400'}`}>
                              {new Date(msg.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-white rounded-xl">
                Nessuna conversazione ancora. Le conversazioni del chatbot appariranno qui.
              </div>
            ) : (
              // Conversations List
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {conversations.map((conv) => (
                    <div
                      key={conv.session_id}
                      onClick={() => fetchConversationDetail(conv.session_id)}
                      className={`px-6 py-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between group ${
                        conv.has_booking_intent ? 'bg-amber-50/50 border-l-4 border-[#d4883a]' : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            conv.has_booking_intent 
                              ? 'bg-[#d4883a]/20' 
                              : 'bg-[#0b114e]/10'
                          }`}>
                            <User className={`w-4 h-4 ${
                              conv.has_booking_intent ? 'text-[#d4883a]' : 'text-[#0b114e]'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900 truncate">
                                {conv.first_user_message || 'Nuova conversazione'}
                              </p>
                              {conv.has_booking_intent && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#d4883a] text-white">
                                  <Star className="w-3 h-3" />
                                  Interessato
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">
                              {conv.message_count} messaggi • {new Date(conv.updated_at).toLocaleString('it-IT')}
                            </p>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-[#d4883a] transition-colors" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'newsletter' ? (
          /* Newsletter View */
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0b114e]">Newsletter</h2>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-[#0b114e]">{newsletterStats.total_subscribers}</p>
                    <p className="text-gray-500 text-sm">Iscritti attivi</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-400">{newsletterStats.unsubscribed}</p>
                    <p className="text-gray-500 text-sm">Disiscritti</p>
                  </div>
                </div>
              </div>
              <div 
                onClick={() => newsletterStats.total_subscribers > 0 && setShowNewsletterForm(true)}
                className={`bg-gradient-to-r from-[#d4883a] to-[#d4896a] rounded-2xl p-6 shadow-sm ${newsletterStats.total_subscribers > 0 ? 'cursor-pointer hover:opacity-90 transition-opacity' : 'opacity-60'}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold">Invia Newsletter</p>
                    <p className="text-white/70 text-sm">
                      {newsletterStats.total_subscribers > 0 ? 'Clicca per comporre' : 'Nessun iscritto'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Newsletter Send Form Modal */}
            {showNewsletterForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowNewsletterForm(false)} />
                <div className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full mx-4 animate-scale-in max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-bold text-[#0b114e] mb-4">Invia Newsletter</h3>
                  <p className="text-gray-500 text-sm mb-6">
                    La newsletter verrà inviata a {newsletterStats.total_subscribers} iscritti attivi.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto</label>
                      <input
                        type="text"
                        value={newsletterSubject}
                        onChange={(e) => { setNewsletterSubject(e.target.value); setNewsletterPreviewAccepted(false); setNewsletterPreview(null); newsletterKey.current = crypto.randomUUID(); }}
                        placeholder="Es: Novità di questa settimana..."
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#d4883a]/20 focus:border-[#d4883a] transition-all"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contenuto</label>
                      <textarea
                        value={newsletterContent}
                        onChange={(e) => { setNewsletterContent(e.target.value); setNewsletterPreviewAccepted(false); setNewsletterPreview(null); newsletterKey.current = crypto.randomUUID(); }}
                        placeholder="Scrivi il contenuto della newsletter..."
                        rows={8}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#d4883a]/20 focus:border-[#d4883a] transition-all resize-none"
                      />
                      <p className="text-xs text-gray-400 mt-1">Puoi usare HTML per la formattazione (es: &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;br&gt;)</p>
                    </div>

                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <p className="text-xs uppercase tracking-wide text-gray-500">Anteprima obbligatoria</p>
                        <p className="font-semibold text-[#0b114e]">{newsletterSubject || 'Oggetto della newsletter'}</p>
                        <p className="text-xs text-gray-500">Destinatari: {newsletterPreview?.total_recipients ?? newsletterStats.total_subscribers} iscritti attivi</p>
                      </div>
                      <iframe
                        title="Anteprima newsletter"
                        sandbox=""
                        srcDoc={newsletterPreview?.content || newsletterContent || '<p style="font-family: sans-serif; color: #666">Il contenuto apparirà qui.</p>'}
                        className="w-full h-48 bg-white"
                      />
                    </div>
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={newsletterPreviewAccepted}
                        onChange={(e) => approveNewsletterPreview(e.target.checked)}
                        className="mt-1"
                      />
                      Ho verificato oggetto, contenuto e numero di destinatari. Accoda l’invio idempotente a piccoli batch.
                    </label>
                  </div>
                  
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowNewsletterForm(false)}
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={sendNewsletter}
                      disabled={sendingNewsletter || !newsletterSubject.trim() || !newsletterContent.trim() || !newsletterPreviewAccepted}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-[#d4883a] to-[#d4896a] text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {sendingNewsletter ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Accodamento...
                        </>
                      ) : (
                        <>
                          <Mail className="w-5 h-5" />
                          Accoda Newsletter
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Subscribers List */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-bold text-[#0b114e]">Lista Iscritti</h3>
              </div>
              {subscribers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  Nessun iscritto ancora. Gli iscritti alla newsletter appariranno qui.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {subscribers.map((subscriber) => (
                    <div key={subscriber.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#0b114e]/10 rounded-full flex items-center justify-center">
                          <Mail className="w-5 h-5 text-[#0b114e]" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{subscriber.email}</p>
                          <p className="text-sm text-gray-500">
                            Iscritto il {new Date(subscriber.subscribed_at || subscriber.consent_at || subscriber.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteSubscriber(subscriber.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'requests' ? (
          /* Contact Requests View */
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0b114e]">Richieste di Contatto</h2>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div 
                onClick={() => setRequestFilter('all')}
                className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all ${requestFilter === 'all' ? 'ring-2 ring-[#d4883a]' : 'hover:shadow-md'}`}
              >
                <p className="text-2xl font-bold text-[#0b114e]">{contactStats.total}</p>
                <p className="text-gray-500 text-sm">Totali</p>
              </div>
              <div 
                onClick={() => setRequestFilter('new')}
                className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all ${requestFilter === 'new' ? 'ring-2 ring-[#d4883a]' : 'hover:shadow-md'}`}
              >
                <p className="text-2xl font-bold text-orange-500">{contactStats.new}</p>
                <p className="text-gray-500 text-sm">Nuove</p>
              </div>
              <div 
                onClick={() => setRequestFilter('read')}
                className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all ${requestFilter === 'read' ? 'ring-2 ring-[#d4883a]' : 'hover:shadow-md'}`}
              >
                <p className="text-2xl font-bold text-blue-500">{contactStats.read}</p>
                <p className="text-gray-500 text-sm">Lette</p>
              </div>
              <div 
                onClick={() => setRequestFilter('contacted')}
                className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all ${requestFilter === 'contacted' ? 'ring-2 ring-[#d4883a]' : 'hover:shadow-md'}`}
              >
                <p className="text-2xl font-bold text-green-500">{contactStats.contacted}</p>
                <p className="text-gray-500 text-sm">Contattati</p>
              </div>
              <div 
                onClick={() => setRequestFilter('archived')}
                className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all ${requestFilter === 'archived' ? 'ring-2 ring-[#d4883a]' : 'hover:shadow-md'}`}
              >
                <p className="text-2xl font-bold text-gray-400">{contactStats.archived}</p>
                <p className="text-gray-500 text-sm">Archiviate</p>
              </div>
            </div>
            
            {/* Request List and Detail View */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* List */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-700">
                    {requestFilter === 'all' ? 'Tutte le richieste' : `Richieste ${requestFilter === 'new' ? 'nuove' : requestFilter === 'read' ? 'lette' : requestFilter === 'contacted' ? 'contattate' : 'archiviate'}`}
                  </h3>
                </div>
                {contactRequests.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    Nessuna richiesta trovata
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                    {contactRequests.map((request) => (
                      <div 
                        key={request.id} 
                        onClick={() => {
                          setSelectedRequest(request);
                          if (request.status === 'new') {
                            updateRequestStatus(request.id, 'read');
                          }
                        }}
                        className={`p-4 cursor-pointer transition-all hover:bg-gray-50 ${selectedRequest?.id === request.id ? 'bg-[#d4883a]/5 border-l-4 border-[#d4883a]' : ''}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-gray-900 truncate">{request.name}</p>
                              {request.status === 'new' && (
                                <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate">{request.company}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(request.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            request.status === 'new' ? 'bg-orange-100 text-orange-700' :
                            request.status === 'read' ? 'bg-blue-100 text-blue-700' :
                            request.status === 'contacted' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {request.status === 'new' ? 'Nuova' : 
                             request.status === 'read' ? 'Letta' : 
                             request.status === 'contacted' ? 'Contattato' : 'Archiviata'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Detail View */}
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {selectedRequest ? (
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold text-[#0b114e] mb-1">{selectedRequest.name}</h3>
                        <a href={`mailto:${selectedRequest.email}`} className="text-[#d4883a] hover:underline">
                          {selectedRequest.email}
                        </a>
                      </div>
                      <button
                        onClick={() => deleteContactRequest(selectedRequest.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="space-y-4 mb-6">
                      <div className="flex items-center gap-3 text-gray-600">
                        <Building2 className="w-5 h-5 text-gray-400" />
                        <span>{selectedRequest.company}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <DollarSign className="w-5 h-5 text-gray-400" />
                        <span>Fatturato: {selectedRequest.revenue}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600">
                        <Clock className="w-5 h-5 text-gray-400" />
                        <span>Ricevuta il {new Date(selectedRequest.created_at).toLocaleDateString('it-IT', { 
                          day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                        })}</span>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Messaggio:</p>
                          <p className="text-gray-600 whitespace-pre-wrap">{selectedRequest.message}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Status Actions */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => updateRequestStatus(selectedRequest.id, 'contacted')}
                        disabled={selectedRequest.status === 'contacted'}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedRequest.status === 'contacted' 
                            ? 'bg-green-100 text-green-700 cursor-default' 
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {selectedRequest.status === 'contacted' ? '✓ Contattato' : 'Segna come Contattato'}
                      </button>
                      <button
                        onClick={() => updateRequestStatus(selectedRequest.id, 'archived')}
                        disabled={selectedRequest.status === 'archived'}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedRequest.status === 'archived' 
                            ? 'bg-gray-200 text-gray-600 cursor-default' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {selectedRequest.status === 'archived' ? '✓ Archiviata' : 'Archivia'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>Seleziona una richiesta per vedere i dettagli</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'retention' ? (
          <RetentionReferences />
        ) : showForm ? (
          <ItemForm
            type={activeTab}
            item={editItem}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditItem(null); }}
            token={token}
          />
        ) : loading ? (
          <div className="text-center py-12 text-gray-500">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Nessun elemento trovato. Clicca "Aggiungi" per crearne uno.
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Titolo</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Stato</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Data</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-600">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{item.title || item.name || item.metric}</p>
                      <p className="text-sm text-gray-500 truncate max-w-md">
                        {item.excerpt || item.text || item.description}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.published !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.published !== false ? 'Pubblicato' : 'Bozza'}
                      </span>
                      {activeTab === 'news' && item.scheduled_at && new Date(item.scheduled_at) > new Date() && (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          📅 {new Date(item.scheduled_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      {activeTab === 'news' && item.featured && (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#d4883a]/10 text-[#d4883a]">
                          <Star className="w-3 h-3" />
                          In evidenza
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(item.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {activeTab === 'news' && (
                          <button
                            onClick={() => toggleFeatured(item.id)}
                            className={`p-2 transition-colors ${item.featured ? 'text-[#d4883a]' : 'text-gray-400 hover:text-[#d4883a]'}`}
                            title={item.featured ? 'Rimuovi da letture consigliate' : 'Aggiungi a letture consigliate'}
                          >
                            <Star className={`w-5 h-5 ${item.featured ? 'fill-current' : ''}`} />
                          </button>
                        )}
                        <button
                          onClick={() => handleTogglePublish(item.id)}
                          className="p-2 text-gray-400 hover:text-[#d4883a] transition-colors"
                          title={item.published !== false ? 'Nascondi' : 'Pubblica'}
                        >
                          {item.published !== false ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => { setEditItem(item); setShowForm(true); }}
                          className="p-2 text-gray-400 hover:text-[#d4883a] transition-colors"
                          title="Modifica"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Form Component
const ItemForm = ({ type, item, onSave, onCancel, token }) => {
  const [formData, setFormData] = useState(item || {});
  const [rawText, setRawText] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // AI Article Generator State
  const [aiKeyword, setAiKeyword] = useState('');
  const [aiCategory, setAiCategory] = useState('Strategia');
  const [generatorMode, setGeneratorMode] = useState('manual'); // 'manual', 'format', 'generate'
  const [generateImage, setGenerateImage] = useState(true); // Generate image with gpt-image-2
  const [imageLoading, setImageLoading] = useState(false); // Loading state for image regeneration
  const [imagePreview, setImagePreview] = useState(item?.imageUrl || null); // Image preview URL
  const [customImagePrompt, setCustomImagePrompt] = useState(''); // Custom prompt for image generation
  const fileInputRef = React.useRef(null);

  const categories = ['Case Study', 'Strategia', 'Business', 'Marketing', 'Growth'];

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleAIFormat = async () => {
    if (!rawText.trim()) return;
    setAiLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/ai/format-news`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ raw_text: rawText }),
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          ...formData,
          title: data.title,
          excerpt: data.excerpt,
          content: data.content,
          category: data.category,
          read_time: data.read_time,
        });
        setRawText('');
        setGeneratorMode('manual');
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail || 'Errore formattazione testo'}`);
      }
    } catch (err) {
      console.error('Errore AI:', err);
      alert('Errore nella formattazione del testo');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiKeyword.trim()) return;
    setAiLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/ai/generate-article`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          keyword: aiKeyword, 
          category: aiCategory,
          generate_image: generateImage 
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const newImageUrl = data.image_url ? absoluteMediaUrl(data.image_url) : formData.imageUrl;
        setFormData({
          ...formData,
          title: data.title,
          excerpt: data.excerpt,
          content: data.content,
          category: data.category,
          read_time: data.read_time,
          meta_description: data.meta_description,
          tags: data.tags,
          imageUrl: newImageUrl,
        });
        if (newImageUrl) {
          setImagePreview(newImageUrl);
        }
        setAiKeyword('');
        setGeneratorMode('manual');
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail || 'Errore generazione articolo'}`);
      }
    } catch (err) {
      console.error('Errore AI:', err);
      alert('Errore nella generazione dell\'articolo');
    } finally {
      setAiLoading(false);
    }
  };

  // Generate or regenerate image with Nano Banana
  const handleGenerateImage = async () => {
    const title = formData.title || aiKeyword || '';
    const category = formData.category || aiCategory || 'Business';
    
    // Check if we have either a custom prompt or a title
    if (!customImagePrompt.trim() && !title.trim()) {
      alert('Inserisci un prompt personalizzato oppure un titolo per generare l\'immagine');
      return;
    }
    
    setImageLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/ai/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          title, 
          category,
          custom_prompt: customImagePrompt.trim() || null
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullImageUrl = absoluteMediaUrl(data.image_url);
        setImagePreview(fullImageUrl);
        setFormData(withArticleImage(formData, fullImageUrl));
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail || 'Errore generazione immagine'}`);
      }
    } catch (err) {
      console.error('Errore generazione immagine:', err);
      alert('Errore nella generazione dell\'immagine');
    } finally {
      setImageLoading(false);
    }
  };

  // Handle image file upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Tipo file non supportato. Usa JPG, PNG o WebP.');
      return;
    }
    
    // Validate file size (max 10MB, aligned with Storage policy)
    if (file.size > 10 * 1024 * 1024) {
      alert('File troppo grande. Dimensione massima: 10MB');
      return;
    }
    
    setImageLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await apiFetch(`${API_URL}/api/admin/upload-image-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullImageUrl = absoluteMediaUrl(data.image_url);
        setImagePreview(fullImageUrl);
        setFormData(withArticleImage(formData, fullImageUrl));
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail || 'Errore upload immagine'}`);
      }
    } catch (err) {
      console.error('Errore upload immagine:', err);
      alert('Errore durante l\'upload dell\'immagine');
    } finally {
      setImageLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const renderFields = () => {
    switch (type) {
      case 'news':
        return (
          <>
            {/* AI Tools Section */}
            <div className="bg-gradient-to-r from-[#0b114e]/5 to-[#d4883a]/5 rounded-xl p-5 mb-6 border border-[#d4883a]/20">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl">🤖</span>
                <h4 className="font-bold text-[#0b114e] text-lg">Assistente AI</h4>
              </div>
              
              {/* Mode Selector */}
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setGeneratorMode('generate')}
                  className={`flex-1 py-2 px-3 rounded-lg font-medium transition-all text-sm ${
                    generatorMode === 'generate'
                      ? 'bg-[#d4883a] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  ✨ Genera Articolo SEO
                </button>
                <button
                  type="button"
                  onClick={() => setGeneratorMode('format')}
                  className={`flex-1 py-2 px-3 rounded-lg font-medium transition-all text-sm ${
                    generatorMode === 'format'
                      ? 'bg-[#d4883a] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  📝 Formatta Testo
                </button>
                <button
                  type="button"
                  onClick={() => setGeneratorMode('manual')}
                  className={`flex-1 py-2 px-3 rounded-lg font-medium transition-all text-sm ${
                    generatorMode === 'manual'
                      ? 'bg-[#d4883a] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  ✍️ Scrittura Manuale
                </button>
              </div>

              {/* Generate Article Mode */}
              {generatorMode === 'generate' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Inserisci una keyword e seleziona la categoria. L'AI genererà un articolo SEO-ottimizzato che ti posiziona come esperto e invoglia a prenotare una call.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Keyword principale</label>
                      <input
                        type="text"
                        value={aiKeyword}
                        onChange={(e) => setAiKeyword(e.target.value)}
                        placeholder="Es: strategia marketing B2B, funnel di vendita..."
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                      <select
                        value={aiCategory}
                        onChange={(e) => setAiCategory(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50 bg-white"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 py-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={generateImage}
                        onChange={(e) => setGenerateImage(e.target.checked)}
                        className="w-5 h-5 text-[#d4883a] border-gray-300 rounded focus:ring-[#d4883a]"
                      />
                      <span className="text-sm text-gray-700">
                        🎨 Genera immagine con AI
                      </span>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleAIGenerate}
                    disabled={aiLoading || !aiKeyword.trim()}
                    className="w-full bg-gradient-to-r from-[#d4883a] to-[#d4896a] hover:from-[#d4896a] hover:to-[#d4883a] text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {aiLoading ? (
                      <>
                        <span className="animate-spin">⏳</span> {generateImage ? 'Generazione articolo + immagine...' : 'Generazione in corso...'}
                      </>
                    ) : (
                      <>
                        🚀 Genera Articolo SEO {generateImage && '+ Immagine'}
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Format Text Mode */}
              {generatorMode === 'format' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Incolla il testo grezzo da Ubersuggest e l'AI lo formatterà automaticamente.
                  </p>
                  <textarea
                    value={rawText}
                    onChange={(e) => setRawText(e.target.value)}
                    placeholder="Incolla qui il testo da formattare..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                  />
                  <button
                    type="button"
                    onClick={handleAIFormat}
                    disabled={aiLoading || !rawText.trim()}
                    className="w-full bg-gradient-to-r from-[#0b114e] to-[#2d4a6f] hover:from-[#2d4a6f] hover:to-[#0b114e] text-white font-semibold py-2.5 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {aiLoading ? (
                      <>
                        <span className="animate-spin">⏳</span> Formattazione in corso...
                      </>
                    ) : (
                      <>
                        ✨ Formatta con AI
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Manual Mode - Just show info */}
              {generatorMode === 'manual' && (
                <p className="text-sm text-gray-500 text-center py-2">
                  Compila i campi sottostanti manualmente oppure usa gli strumenti AI sopra.
                </p>
              )}

              {/* Image Generation Section - Available in all modes */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h5 className="font-semibold text-[#0b114e] flex items-center gap-2 mb-3">
                  🎨 Immagine Articolo
                </h5>
                
                {/* Custom Prompt Input */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prompt personalizzato (opzionale)
                  </label>
                  <textarea
                    value={customImagePrompt}
                    onChange={(e) => setCustomImagePrompt(e.target.value)}
                    placeholder="Es: Un'immagine astratta con sfumature blu e arancio che rappresenta la crescita aziendale, stile minimalista..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500/50 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Se vuoto, verrà generato automaticamente dal titolo
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={handleGenerateImage}
                    disabled={imageLoading || (!formData.title && !aiKeyword && !customImagePrompt.trim())}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-pink-500 hover:to-purple-500 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {imageLoading ? (
                      <>
                        <span className="animate-spin">⏳</span> Generazione...
                      </>
                    ) : imagePreview ? (
                      <>
                        🔄 Rigenera con AI
                      </>
                    ) : (
                      <>
                        ✨ Genera con AI
                      </>
                    )}
                  </button>
                  
                  <label className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-all cursor-pointer flex items-center justify-center gap-2 border border-gray-300">
                    📁 Carica Immagine
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                
                {/* Image Preview */}
                {imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    <img 
                      src={imagePreview} 
                      alt="Anteprima immagine articolo" 
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-48 items-center justify-center text-gray-400">
                      Errore caricamento immagine
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setFormData(withArticleImage(formData, ''));
                      }}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full transition-colors"
                      title="Rimuovi immagine"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-400">
                    <p className="text-sm">Nessuna immagine</p>
                    <p className="text-xs mt-1">Genera con AI o carica un file (JPG, PNG o WebP - max 10MB)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Standard Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estratto</label>
              <input
                type="text"
                value={formData.excerpt || ''}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenuto</label>
              <textarea
                value={formData.content || ''}
                onChange={(e) => handleChange('content', e.target.value)}
                rows={10}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50 font-mono text-sm"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Usa ## per titoli H2 e ### per titoli H3</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={formData.category || 'Marketing'}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50 bg-white"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tempo lettura</label>
                <input
                  type="text"
                  value={formData.read_time || '5 min'}
                  onChange={(e) => handleChange('read_time', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                />
              </div>
            </div>
            
            {/* SEO Fields */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h5 className="font-semibold text-[#0b114e] mb-3 flex items-center gap-2">
                <span>🔍</span> SEO
              </h5>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                  <input
                    type="text"
                    value={formData.meta_description || ''}
                    onChange={(e) => handleChange('meta_description', e.target.value)}
                    placeholder="Descrizione per i motori di ricerca (max 155 caratteri)"
                    maxLength={155}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                  />
                  <p className="text-xs text-gray-500 mt-1">{(formData.meta_description || '').length}/155 caratteri</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (separati da virgola)</label>
                  <input
                    type="text"
                    value={(formData.tags || []).join(', ')}
                    onChange={(e) => handleChange('tags', e.target.value.split(',').map(t => t.trim()).filter(t => t))}
                    placeholder="marketing, strategia, B2B, crescita"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                  />
                </div>
              </div>
            </div>

            {/* Scheduled Publishing */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h5 className="font-semibold text-[#0b114e] mb-3 flex items-center gap-2">
                <span>📅</span> Programmazione Pubblicazione
              </h5>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data e ora di pubblicazione</label>
                  <input
                    type="datetime-local"
                    value={localDateTimeValue(formData.scheduled_at)}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        // Convert to ISO string with timezone
                        const date = new Date(value);
                        handleChange('scheduled_at', date.toISOString());
                      } else {
                        handleChange('scheduled_at', null);
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.scheduled_at 
                      ? `Pubblicazione programmata: ${new Date(formData.scheduled_at).toLocaleString('it-IT')}`
                      : 'Senza data, l’articolo resta una bozza finché non scegli Pubblica'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL Immagine (opzionale)</label>
              <input
                type="url"
                value={formData.imageUrl ?? formData.image_url ?? ''}
                onChange={(e) => {
                  setFormData(withArticleImage(formData, e.target.value));
                  setImagePreview(e.target.value || null);
                }}
                placeholder="https://..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
              />
            </div>
          </>
        );

      case 'testimonials':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
                <input
                  type="text"
                  value={formData.role || ''}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Azienda</label>
              <input
                type="text"
                value={formData.company || ''}
                onChange={(e) => handleChange('company', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Testimonianza</label>
              <textarea
                value={formData.text || ''}
                onChange={(e) => handleChange('text', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rating (1-5)</label>
              <input
                type="number"
                min="1"
                max="5"
                value={formData.rating || 5}
                onChange={(e) => handleChange('rating', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
              />
            </div>
          </>
        );

      case 'services':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo (es: "da €3.000/mese")</label>
              <input
                type="text"
                value={formData.price || ''}
                onChange={(e) => handleChange('price', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Features (una per riga)</label>
              <textarea
                value={(formData.features || []).join('\n')}
                onChange={(e) => handleChange('features', e.target.value.split('\n').filter(f => f.trim()))}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.popular || false}
                onChange={(e) => handleChange('popular', e.target.checked)}
                className="w-4 h-4 text-[#d4883a] rounded focus:ring-[#d4883a]"
              />
              <label className="text-sm text-gray-700">Servizio in evidenza</label>
            </div>
          </>
        );

      case 'results':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Metrica (es: "+180%")</label>
              <input
                type="text"
                value={formData.metric || ''}
                onChange={(e) => handleChange('metric', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50"
                required
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-xl font-bold text-[#0b114e] mb-6">
        {item ? 'Modifica' : 'Nuovo'} {type.slice(0, -1)}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {renderFields()}
        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            className="flex-1 bg-[#d4883a] hover:bg-[#d4896a] text-white font-semibold py-3 rounded-lg transition-colors"
          >
            Salva
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 rounded-lg transition-colors"
          >
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
};

// Main Admin Page
const AdminPage = () => {
  const supabaseMode = dataBackend === 'supabase' && supabase;
  const [token, setToken] = useState(supabaseMode ? null : localStorage.getItem('admin_token'));

  useEffect(() => {
    if (supabaseMode) {
      let active = true;
      supabase.auth.getSession().then(async ({ data }) => {
        if (!active || !data.session) return;
        const assurance = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (active && assurance.data?.currentLevel === 'aal2') setToken(data.session.access_token);
      });
      const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
        if (!session) setToken(null);
      });
      return () => {
        active = false;
        listener.subscription.unsubscribe();
      };
    }
    // Verifica se il token è valido
    if (token) {
      apiFetch(`${API_URL}/api/admin/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then((res) => {
          if (!res.ok) {
            if (!supabaseMode) localStorage.removeItem('admin_token');
            setToken(null);
          }
        })
        .catch(() => {
          if (!supabaseMode) localStorage.removeItem('admin_token');
          setToken(null);
        });
    }
  }, [token, supabaseMode]);

  const handleLogout = async () => {
    if (supabaseMode) await supabase.auth.signOut();
    else localStorage.removeItem('admin_token');
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={setToken} />;
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
};

export default AdminPage;
